
import {
  defineComponent,
  ref,
  computed,
  toRef,
  onMounted,
  onBeforeUnmount,
} from "vue";
import { Icon } from "@iconify/vue";
import { useTimerAssistant } from "@/composables/useTimerAssistant";
import { Theme, ProjectRole } from "@/helpers/project/typesDefinition";

export interface StartTimerPayload {
  activityId: string;
  activityName: string;
  project?: string;
  projectId?: string;
  activitySlug?: string;
}

export default defineComponent({
  components: {
    Icon,
  },
  props: {
    userId: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const {
      currentTimer,
      isExpanded,
      themesLoading,
      formattedTime,
      toggleExpanded,
      startTimer,
      stopTimer,
      restartTimer,
      deleteTimer,
      createTimesheet,
      filterThemes,
      availableRoles,
      rolesLoading,
      setSelectedRole,
    } = useTimerAssistant({
      userId: toRef(props, "userId"),
    });

    const themeSearch = ref("");
    const showThemeSelector = ref(false);
    const pendingNotes = ref<string | undefined>(undefined);
    const pendingActivityId = ref<string | undefined>(undefined);
    const projectIdFilter = ref<string | undefined>(undefined);

    const filteredThemes = computed(() => {
      const themes = filterThemes(themeSearch.value);

      if (projectIdFilter.value) {
        const normalizedFilterId = projectIdFilter.value.trim().toLowerCase();

        const filtered = themes.filter(
          (t: Theme) =>
            t.project &&
            t.project.id?.trim().toLowerCase() === normalizedFilterId,
        );

        // If no matches, return all themes (fallback)
        return filtered.length > 0 ? filtered : themes;
      }
      return themes;
    });

    const currentThemeName = computed(() => {
      if (currentTimer.value?.theme) {
        return `${currentTimer.value.theme.project?.name} - ${currentTimer.value.theme.name}`;
      }
      return null;
    });

    const selectedRole = computed({
      get: (): ProjectRole | undefined => currentTimer.value?.role,
      set: (role: ProjectRole | undefined) => {
        if (role) setSelectedRole(role);
      },
    });

    const handleStartTimer = async () => {
      if (currentTimer.value?.running) {
        await stopTimer();
      } else if (currentTimer.value) {
        await restartTimer();
        window.dispatchEvent(new CustomEvent("fides:timerStarted"));
      } else {
        showThemeSelector.value = true;
      }
    };

    const handleSelectTheme = async (theme: Theme) => {
      showThemeSelector.value = false;
      themeSearch.value = "";
      await startTimer(theme);
      window.dispatchEvent(new CustomEvent("fides:timerStarted"));
    };

    const handleCreateTimesheet = async () => {
      await createTimesheet();
    };

    const handleCloseThemeSelector = () => {
      showThemeSelector.value = false;
      themeSearch.value = "";
    };

    const openThemeSelector = () => {
      showThemeSelector.value = true;
    };

    // Handler for external timer start via custom event (from ProjectOverview or other Fides pages)
    const handleStartTimerFromActivity = async (event: Event) => {
      const customEvent = event as CustomEvent<StartTimerPayload>;
      const payload: StartTimerPayload = customEvent.detail;

      console.log("[FloatingTimer] Received fides:startTimer event:", payload);

      const { activityId, activityName, projectId, project, activitySlug } =
        payload;

      console.log(
        "[FloatingTimer] activityId:",
        activityId,
        "activityName:",
        activityName,
        "projectId:",
        projectId,
        "project:",
        project,
        "activitySlug:",
        activitySlug,
      );

      if (currentTimer.value?.running) {
        await stopTimer();
      }
      projectIdFilter.value = projectId;
      pendingNotes.value = activitySlug
        ? `${activitySlug}: ${activityName}`
        : activityName;
      pendingActivityId.value = activityId;
      showThemeSelector.value = true;

      console.log(
        "[FloatingTimer] Set projectIdFilter to:",
        projectIdFilter.value,
        "pendingActivityId to:",
        pendingActivityId.value,
      );
    };

    const handleSelectThemeWithNotes = async (theme: Theme) => {
      showThemeSelector.value = false;
      themeSearch.value = "";
      const notes = pendingNotes.value;
      const activityId = pendingActivityId.value;
      pendingNotes.value = undefined;
      pendingActivityId.value = undefined;
      projectIdFilter.value = undefined;
      await startTimer(theme, notes, activityId);
      window.dispatchEvent(new CustomEvent("fides:timerStarted"));
    };

    onMounted(() => {
      window.addEventListener("fides:startTimer", handleStartTimerFromActivity);
    });

    onBeforeUnmount(() => {
      window.removeEventListener(
        "fides:startTimer",
        handleStartTimerFromActivity,
      );
    });

    return {
      currentTimer,
      isExpanded,
      themesLoading,
      formattedTime,
      toggleExpanded,
      startTimer,
      stopTimer,
      restartTimer,
      deleteTimer,
      createTimesheet,
      filterThemes,
      themeSearch,
      showThemeSelector,
      filteredThemes,
      currentThemeName,
      handleStartTimer,
      handleSelectTheme,
      handleSelectThemeWithNotes,
      handleCreateTimesheet,
      handleCloseThemeSelector,
      openThemeSelector,
      availableRoles,
      rolesLoading,
      selectedRole,
    };
  },
});
